import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CreditCard, Loader2, ShieldCheck, LogOut } from "lucide-react";
import TermsDialog from "@/components/TermsDialog";

const PERKS = [
  "14 days free — your card is not charged today",
  "Cancel any time before day 14 and you pay nothing",
  "Every feature unlocked from the first minute",
];

export const SubscriptionRequired = () => {
  const { org, user, logout, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [termsOpen, setTermsOpen] = useState(false);
  const canPay = (user?.permissions || []).includes("billing:manage");

  const start = async () => {
    setTermsOpen(false);
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/plans/start-trial", { origin_url: window.location.origin });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      await refresh();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not open checkout");
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="subscription-required"
      className="min-h-screen bg-royal-deep sidebar-grain flex items-center justify-center p-6"
    >
      <div className="w-full max-w-lg rounded-2xl bg-white p-8 sm:p-10 rise">
        <span className="inline-flex items-center gap-2 rounded-full bg-gold/15 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-royal-deep">
          <ShieldCheck className="h-3.5 w-3.5" /> One step left
        </span>
        <h1 className="font-display text-3xl font-bold text-royal-deep mt-5">
          Activate {org?.name || "your company"}
        </h1>
        <p className="text-sm text-muted-foreground mt-3">
          Cornerstone Ops keeps a card on file so your trial rolls straight into service with no
          interruption to your crews. Nothing is charged for 14 days.
        </p>
        <ul className="mt-6 space-y-2.5">
          {PERKS.map((p) => (
            <li key={p} className="flex items-start gap-2.5 text-sm text-royal-deep">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />
              {p}
            </li>
          ))}
        </ul>

        {canPay ? (
          <Button
            data-testid="start-trial-btn"
            onClick={() => setTermsOpen(true)}
            disabled={busy}
            className="mt-8 w-full min-h-[48px] bg-royal hover:bg-royal-deep text-white font-semibold"
          >
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <CreditCard className="h-4 w-4 mr-2" /> Add payment method & start trial
              </>
            )}
          </Button>
        ) : (
          <p
            data-testid="subscription-required-not-owner"
            className="mt-8 rounded-lg bg-muted p-4 text-sm text-muted-foreground"
          >
            Ask the owner of {org?.name || "your company"} to add a payment method — the account
            unlocks for the whole team the moment they do.
          </p>
        )}

        {error && (
          <p data-testid="subscription-required-error" className="mt-4 text-sm text-red-600">
            {error}
          </p>
        )}

        <button
          data-testid="subscription-required-logout"
          onClick={logout}
          className="mt-6 inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-royal transition-colors duration-200"
        >
          <LogOut className="h-3.5 w-3.5" /> Sign out
        </button>

        <p className="mt-5 text-[11px] text-muted-foreground">
          By activating you accept our{" "}
          <a href="/terms" target="_blank" rel="noreferrer" data-testid="activate-terms-link"
             className="font-semibold text-royal underline">Terms of Service</a> and{" "}
          <a href="/privacy" target="_blank" rel="noreferrer"
             className="font-semibold text-royal underline">Privacy Policy</a>.
        </p>
      </div>

      <TermsDialog open={termsOpen} onClose={() => setTermsOpen(false)} onAccepted={start}
        context="activation" />
    </div>
  );
};

export default SubscriptionRequired;
